import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { closeSubModal } from "../../features/modal/ModalSlice";
import { IoClose } from "react-icons/io5";
import { Accordion } from "react-bootstrap";
import { VscCallIncoming, VscCallOutgoing } from "react-icons/vsc";
import { FaPlus } from "react-icons/fa6";
import { MdOutlineSubtitles, MdOutlineTitle } from "react-icons/md";
import CustomButton from "../Button/CustomButton";
import apiClient from "../../services/apiClient";
import { updateSipState } from "../../features/SIP/SIPCallsSlice";
import toast from "react-hot-toast";
import { formatDate } from "../../utils/utils";
import { CircularProgress } from "@mui/material";

const ActivityPopUp = () => {
  const [callLogs, setCallLogs] = useState([]);
  const [addNote, setAddNote] = useState(false);
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [callLogsLoading, setCallLogsLoading] = useState(false);
  const [transcripts, setTranscripts] = useState([]);
  const transcriptContainerRef = useRef(null);

  const [filter, setFilter] = useState({
    start_date: "",
    end_date: "",
    call_direction: "",
    callerid: "",
    callednum: "",
    callerid_and_or: "",
    callednum_and_or: "",
    forward_to: "",
    page: 1,
    limit: 10,
    OrgId: "",
  });

  const dispatch = useDispatch();
  const SIPCalls = useSelector((state) => state?.SIPCalls);
  const userInfo = useSelector((state) => state?.userInfo.user);

  const wsRef = useRef(null); // 🔧 Added ref to track WebSocket instance

  useEffect(() => {
    fetchCallLogs();
  }, []);

  // 2️⃣  add the auto‑scroll effect
useEffect(() => {
  // If there is at least one transcript, scroll to the bottom
  if (!transcriptContainerRef.current) return;

  // Using scrollTop = scrollHeight is the most straightforward way
  // (works for a flex column container as well)
  transcriptContainerRef.current.scrollTo({
  top: transcriptContainerRef.current.scrollHeight,
  behavior: 'smooth',
});


  // Optional: smooth scrolling
  // transcriptContainerRef.current.scrollTo({
  //   top: transcriptContainerRef.current.scrollHeight,
  //   behavior: 'smooth',
  // });
}, [transcripts]);   // runs every time a new transcript is added

  const fetchCallLogs = async (page = 1) => {
    try {
      setCallLogsLoading(true);
      let payload = {
        ...filter,
        page,
      };

      const response = await apiClient.post(
        `${process.env.REACT_APP_VOIP_SERVER_BASE_URL}/calls`,
        payload
      );
      const newLogs = response?.data || [];
      setCallLogs(newLogs);
    } catch (err) {
      console.log(err);
      toast.error("Network Error");
    } finally {
      setCallLogsLoading(false);
    }
  };

  // ------------------------------
  // 🔹 WebSocket for Real-Time Transcript
  // ------------------------------
  useEffect(() => {
    const wsUrl = process.env.REACT_APP_VOIP_WEBSOCKET_BASE_URL_TRANSCRIPT;
    if (!wsUrl) {
      console.error("❌ WebSocket URL not defined");
      return;
    }

    // 🔧 Close existing socket if already connected before reconnecting
    if (wsRef.current) {
      wsRef.current.close();
    }

    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    console.log("Connecting WebSocket:", wsUrl);
    setTranscripts([]); // 🔧 Clear transcript on new connection

    ws.onopen = () => {
      console.log("✅ Connected to WebSocket:", wsUrl);
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log("📩 WebSocket Data:", data);

        // ✅ Check if call ended
        if (data?.text === "__CALL_ENDED__" && data?.channel === "system") {
          console.log("☎️ Call ended — resetting transcript...");

          // Display analysis if available
          if (data?.analysis) {
            console.log("📊 Call Analysis:", data.analysis);
            // Optionally add analysis to transcripts for display
            setTranscripts((prev) => [
              ...prev,
              {
                speaker: "System",
                text: `Call Summary: ${data.analysis.summary}`,
                timestamp: data.timestamp,
                isAnalysis: true
              },
            ]);
          }

          // Don't clear transcripts immediately so user can see the summary
          // setTranscripts([]);
          // ws.close();
          return;
        }

        // ✅ Handle regular transcript messages (User or Agent channel)
        if (data?.text && (data?.channel === "User" || data?.channel === "Agent")) {
          const speaker = data.channel === "User" ? "User" : "Agent";

          setTranscripts((prev) => [
            ...prev,
            { speaker, text: data.text, timestamp: data.timestamp },
          ]);
        }
      } catch (err) {
        console.log("Invalid WebSocket message:", event.data);
      }
    };

    ws.onclose = () => {
      console.log("❌ WebSocket disconnected");
    };

    return () => {
      console.log("🔻 Closing WebSocket connection");
      ws.close();
    };
  }, [SIPCalls?.sipRecentCallUUID]); // 🔧 reconnect whenever call changes

  // 🔧 When call ends from Redux state, reset transcript manually
  useEffect(() => {
    if (!SIPCalls?.isCallActive) {
      console.log("🛑 Call ended — clearing transcript");
      setTranscripts([]);
    }
  }, [SIPCalls?.isCallActive]);

  const toggleNotes = () => setAddNote(!addNote);
  const toggleActivitie = () => dispatch(closeSubModal());

  const handleSaveNote = async () => {
    try {
      setLoading(true);
      const res = await apiClient.post(
        `${process.env.REACT_APP_VOIP_SERVER_BASE_URL}/calls/add-notes`,
        {
          callId: SIPCalls?.sipRecentCallUUID,
          notes: notes,
        }
      );
      dispatch(updateSipState({ key: "sipRecentCallUUID", value: "" }));
      toggleNotes();
      setNotes("");
      toast.success("Notes Added!");
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="activitie">
      <div className="activitieScree">
        <h4 className="closeTitle d-flex justify-content-between">
          Transcript
          <button onClick={toggleActivitie} className="close">
            <IoClose />
          </button>
        </h4>

        {/* Split Screen: Left = Call Logs & Notes | Right = Real-Time Transcript */}
        <div className="px-4 py-2 d-flex gap-3">
          {/* LEFT SIDE */}
          {/* <div style={{ flex: 1 }}>
            <div className="transparentBtn d-flex mb-3">
              <button className="ms-auto" onClick={toggleNotes}>
                <span className="me-1">
                  <FaPlus />
                </span>{" "}
                Add a note
              </button>
            </div>

            <div className={`${addNote ? "active" : ""} addNotes`}>
              <div className="notesForm mb-2">
                <div className="inputData withIcon mb-4">
                  <span className="icon">
                    <MdOutlineTitle />
                  </span>
                  <input className="form-control" type="text" required />
                  <label className="label">Notes Title</label>
                </div>

                <div className="inputData textareaData withIcon mb-2">
                  <span className="icon">
                    <MdOutlineSubtitles />
                  </span>
                  <textarea
                    className="form-control h-auto"
                    rows="4"
                    cols="80"
                    required
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                  />
                  <label className="label">Type your notes here</label>
                </div>
                <div className="text-end mb-3">
                  <CustomButton
                    text="Submit"
                    style={{ borderRadius: "30px", padding: "4px 24px" }}
                    onClick={handleSaveNote}
                    disabled={!SIPCalls?.sipRecentCallUUID}
                    loading={loading}
                  />
                </div>
              </div>

              <div className="cstAccordion">
                <div className="text-center">
                  {callLogsLoading && <CircularProgress />}
                </div>
                <Accordion defaultActiveKey="0" flush>
                  {callLogs?.map((log, index) => (
                    <Accordion.Item eventKey={index} key={index}>
                      <Accordion.Header>
                        <span className="callIcon">
                          {log?.call_direction ? (
                            <VscCallOutgoing />
                          ) : (
                            <VscCallIncoming />
                          )}
                        </span>{" "}
                        {formatDate(log?.callstart)}
                      </Accordion.Header>
                      <Accordion.Body>
                        <div className="addNote">
                          <div className="yourNote">
                            <span>
                              <MdOutlineSubtitles />
                            </span>{" "}
                            Notes
                          </div>
                          <p>{log?.custom_disposition || "No Notes"}</p>
                        </div>
                      </Accordion.Body>
                    </Accordion.Item>
                  ))}
                </Accordion>
              </div>
            </div>
          </div> */}

          {/* RIGHT SIDE - Chat-like Real-time Transcript */}
          {/* RIGHT SIDE - WhatsApp-style Transcript */}
          <div
          ref={transcriptContainerRef}
            style={{
              flex: 1,
              borderLeft: "1px solid #ddd",
              paddingLeft: "20px",
              maxHeight: "90vh",
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <h5>💬 Live Transcript</h5>

            {transcripts.length > 0 ? (
              transcripts.map((t, i) => (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    justifyContent:
                      t.speaker === "User" ? "flex-end" :
                      t.isAnalysis ? "center" : "flex-start",
                    marginBottom: "10px",
                  }}
                >
                  <div
                    style={{
                      background: t.isAnalysis ? "#e3f2fd" :
                                 t.speaker === "User" ? "#dcf8c6" : "#ffffff", // WhatsApp color scheme
                      padding: "10px 14px",
                      borderRadius: "15px",
                      maxWidth: t.isAnalysis ? "85%" : "70%",
                      boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                      wordWrap: "break-word",
                      border: t.isAnalysis ? "1px solid #90caf9" : "none",
                    }}
                  >
                    <strong
                      style={{
                        display: "block",
                        fontSize: "0.8rem",
                        color: t.isAnalysis ? "#1976d2" : "#555",
                        marginBottom: "2px",
                      }}
                    >
                      {t.speaker}
                    </strong>
                    <span style={{ fontSize: "0.9rem" }}>{t.text}</span>
                    <div
                      style={{
                        textAlign: "right",
                        fontSize: "0.7rem",
                        color: "#999",
                        marginTop: "4px",
                      }}
                    >
                      {new Date(t.timestamp).toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-muted">Waiting for live transcript...</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActivityPopUp;
